import { Form, Input, InputNumber, Select, Space } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { FilterOperatorEnum } from 'domains/appDomain';
import {
  SvcFilter,
  UOMCoefficientOptions,
  UOMOptions,
} from 'domains/svcDomain';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  normalizeFilterObj,
  normalizeToFormFields,
  normalizeValues,
} from 'utils/filter';
import { FilterOperatorSelect } from '../common/FilterOperatorSelect';
import { FormItemForFilterValues } from '../common/FormItemForFilterValues';
import {
  isSingleOperator,
  MARKUP_OPERATORS,
  NUMERIC_OPERATORS,
} from '../common/utils';

export const SVCFilterForm = () => {
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const filterObj = useAppSelector((state) => state.svc.filterObj);
  const loading = useAppSelector(
    (state) => state.loading.effects.svc.getAllSVCs
  );
  const [markupOperator, setMarkupOperator] = useState(
    filterObj.markupPct?.operator
  );
  const [matMarkupOperator, setMatMarkupOperator] = useState(
    filterObj.materialMarkupPct?.operator
  );

  const onFinish = (values: SvcFilter) => {
    const filterObj = normalizeFilterObj(values);
    console.log(filterObj);
    dispatch.svc.saveFilter(filterObj);
  };

  useEffect(() => {
    form.setFieldsValue(normalizeToFormFields(filterObj));
    setMarkupOperator(filterObj.markupPct?.operator);
    setMatMarkupOperator(filterObj.materialMarkupPct?.operator);
  }, [filterObj, form]);

  const clearFilters = () => {
    dispatch.svc.removeFilter();
    form.resetFields();
  };

  const svcTypes = useAppSelector((state) => state.svc.svcTypes);

  return (
    <RevxSpin spinning={loading}>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
        onValuesChange={(values) => {
          console.log('onValues Change', values);
        }}
      >
        <Form.Item label={t('platform.filter.formLabel.svcRevxId')}>
          <Form.Item
            noStyle
            name={['svcRevxId', 'operator']}
            initialValue={FilterOperatorEnum.EQUALS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['svcRevxId', 'values']}>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              controls={false}
              maxLength={15}
              max={Number.MAX_SAFE_INTEGER}
            />
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.filter.formLabel.specRevxId')}>
          <Form.Item
            noStyle
            name={['specRevxId', 'operator']}
            initialValue={FilterOperatorEnum.EQUALS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['specRevxId', 'values']}>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              controls={false}
              maxLength={15}
              max={Number.MAX_SAFE_INTEGER}
            />
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.filter.formLabel.serviceType')}>
          <Form.Item
            noStyle
            name={['svcTypeId', 'operator']}
            initialValue={FilterOperatorEnum.IN}
          >
            <Input hidden />
          </Form.Item>

          <FormItemForFilterValues noStyle name={['svcTypeId', 'values']}>
            <Select
              mode="multiple"
              showSearch
              filterOption={(value, option) => {
                return String(option?.children)
                  .toLocaleLowerCase()
                  .includes(value.toLocaleLowerCase());
              }}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              {svcTypes.map((type) => {
                return (
                  <Select.Option value={type.id} key={type.id}>
                    {type.name}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.filter.formLabel.serviceName')}>
          <Form.Item
            noStyle
            name={['description', 'operator']}
            initialValue={FilterOperatorEnum.CONTAINS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['description', 'values']}>
            <Input />
          </FormItemForFilterValues>
        </Form.Item>

        <Form.Item label={t('platform.filter.formLabel.uom')}>
          <Form.Item
            noStyle
            name={['uom', 'operator']}
            initialValue={FilterOperatorEnum.IN}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['uom', 'values']}>
            <Select
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
              allowClear
              mode="multiple"
            >
              {UOMOptions.map((opt) => {
                return (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.name}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item label={t('platform.filter.formLabel.uomCoefficient')}>
          <Form.Item
            noStyle
            name={['uomCoefficient', 'operator']}
            initialValue={FilterOperatorEnum.IN}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['uomCoefficient', 'values']}>
            <Select
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
              mode="multiple"
              allowClear
            >
              {UOMCoefficientOptions.map((option) => {
                return (
                  <Select.Option key={option.value} value={option.value}>
                    {option.name}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.laborCostUSD')}
          name={['costUsd', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            addonBefore={
              <Form.Item
                name={['costUsd', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.makereadyCostUSD')}
          name={['makereadyCostUsd', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            addonBefore={
              <Form.Item
                name={['makereadyCostUsd', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.laborMarkupPCT')}
          name={['markupPct', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            disabled={markupOperator && isSingleOperator(markupOperator)}
            addonBefore={
              <Form.Item
                name={['markupPct', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect
                  operatorOptions={MARKUP_OPERATORS}
                  onChange={(val) => {
                    setMarkupOperator(val as FilterOperatorEnum);

                    if (isSingleOperator(val as FilterOperatorEnum)) {
                      form.setFieldsValue({
                        markupPct: {
                          values: [],
                          operator: val,
                        },
                      });
                    }
                  }}
                />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.matMarkupPCT')}
          name={['materialMarkupPct', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            disabled={matMarkupOperator && isSingleOperator(matMarkupOperator)}
            addonBefore={
              <Form.Item
                name={['materialMarkupPct', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect
                  operatorOptions={MARKUP_OPERATORS}
                  onChange={(val) => {
                    setMatMarkupOperator(val as FilterOperatorEnum);

                    if (isSingleOperator(val as FilterOperatorEnum)) {
                      form.setFieldsValue({
                        materialMarkupPct: {
                          values: [],
                          operator: val,
                        },
                      });
                    }
                  }}
                />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.matCostUSD')}
          name={['materialCostUsd', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            addonBefore={
              <Form.Item
                name={['materialCostUsd', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>

        <Form.Item
          label={t('platform.filter.formLabel.timeHR')}
          name={['timeHr', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            addonBefore={
              <Form.Item
                name={['timeHr', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.makereadyTimeHR')}
          name={['makereadyTimeHr', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            addonBefore={
              <Form.Item
                name={['makereadyTimeHr', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <RevxButton
              type="primary"
              brandPurple
              htmlType="submit"
              brandLightPurple
              className="admin-filter-btn"
            >
              {t('platform.filter.filterBtnLabel')}
            </RevxButton>
            <RevxButton
              brandGreen
              className="admin-clear-filter-btn"
              onClick={() => {
                clearFilters();
              }}
            >
              {t('platform.filter.clearFilterBtnLabel')}
            </RevxButton>
          </Space>
        </Form.Item>
      </Form>
    </RevxSpin>
  );
};
